import { ROUTES } from '@/app/routes';
import VbivStatus from '@/components/vbiv-status';
import type { IPhoneListItem } from '@/entities/phone.entity';
import { CalendarClockIcon, CircleDollarSignIcon, ContainerIcon, HashIcon, PhoneIcon, TagIcon } from 'lucide-react';
import { memo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);
const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

const STATUS_LABEL: Record<number, { text: string; className: string }> = {
	0: { text: 'Нет номера', className: 'text-muted-foreground' },
	1: { text: 'Команда создана', className: 'text-[#ff9d00]' },
	2: { text: 'Команда получена', className: 'text-green-500' },
	3: { text: 'Ошибка', className: 'text-red-500' },
};

const PhoneListItem = memo(function PhoneListItem({ phone }: { phone: IPhoneListItem }) {
	const navigate = useNavigate();

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	const status = STATUS_LABEL[phone.status];
	const cardBalances = phone.cardBalances?.filter(b => b != null && b !== 0) ?? [];

	return (
		<div
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/40'
			onClick={() => navigate(ROUTES.client + phone.clientId)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					navigate(ROUTES.client + phone.clientId);
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<PhoneIcon size={16} className='opacity-40' />
					</div>
					{phone.number ? (
						<span
							className='text-sm hover:bg-accent cursor-pointer select-none text-chart-3 font-semibold'
							onClick={e => handleCopy(e, phone.number || '')}
						>
							{phone.number}
						</span>
					) : (
						<span className='text-sm text-muted-foreground'>Без номера</span>
					)}
				</span>
				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={phone.clientId} takeStatus={phone.takeStatus} takenFrom={phone.takenFrom} />
				</div>
			</div>

			{(phone.name || phone.subId) && (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<TagIcon size={16} className='opacity-40' />
						</div>
						<span className='text-sm'>
							{phone.name}
							{phone.subId ? <span className='text-muted-foreground ml-1'>#{phone.subId}</span> : null}
						</span>
					</span>
				</div>
			)}

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + phone.clientId)}>
						{phone.clientId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, phone.buildId)}>
						{phone.buildId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<CircleDollarSignIcon size={16} className='opacity-40' />
					</div>
					{cardBalances.length ? (
						<div className='flex gap-2 flex-wrap'>
							{cardBalances.map((balance, i) => (
								<span
									key={`${balance}-${i}`}
									className='mr-1 font-semibold text-sm hover:bg-accent cursor-pointer select-none break-all text-destructive'
									onClick={e => handleCopy(e, balance.toString())}
								>
									{money.format(balance)}
								</span>
							))}
						</div>
					) : (
						<span className='opacity-60 text-sm'>Нет Балансов</span>
					)}
				</span>
			</div>

			{status && !phone.number && (
				<div className='flex justify-between'>
					<span className={`text-sm ${status.className}`}>{status.text}</span>
				</div>
			)}

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40'>Создан</span>
				<div className='text-sm text-chart-2 opacity-95'>
					<TimeAgo date={phone.createdAt} formatter={formatter} />
				</div>
			</span>
		</div>
	);
});

export default PhoneListItem;
